import { IPersonBadge } from "../../../types";
import { UpgradeCompleteOptions } from "@reedexpo/mercury-badge-manager-components";
import { ReceiptProduct, UpgradeReceiptParams } from "../receipts.types";
import EventHelper from "../../../network/helpers/event/EventHelper";
import CurrencyHelper from "../../../helpers/currency/CurrencyHelper";
import MagentoHelper from "../../../network/helpers/magento/MagentoHelper";
import { useSelector } from "react-redux";
import { badgeOrdersSelector, badgesSelector } from "../../../../redux/eventEdition/selectors/eventEdition.selectors";
import { productsSelector } from "../../../../redux/magento/selectors/magento.selectors";
import { PaymentMethods } from "../../../enums";
import { ReceiptBuilderProps } from "@reedexpo/mercury-badge";
import { useTranslation } from "react-i18next";

export const useReceiptPropGeneration = () => {
  const badges: IPersonBadge[] = useSelector(badgesSelector);
  const products = useSelector(productsSelector);
  const orders = useSelector(badgeOrdersSelector);
  const { i18n } = useTranslation();

  const getMappedPaymentMethod = (paymentMethod: string) => {
    if (paymentMethod === "onsite_visa_master_other" || paymentMethod === "onsite_amex") {
      return PaymentMethods.CARD;
    }
    if (paymentMethod === "onsite_debit") {
      return PaymentMethods.DEBIT_CARD;
    }
    if (paymentMethod === "onsite_cheque") {
      return PaymentMethods.CHEQUE;
    }
    if (paymentMethod === "onsite_invoice_credit") {
      return PaymentMethods.INVOICE_CREDIT;
    }
    return PaymentMethods.CASH;
  };

  const getBadgeFromStoreById = (badgeId: string) => {
    return badges.find(storeBadge => storeBadge.id === badgeId);
  };

  const getPropsForPartialPayments = (options: UpgradeCompleteOptions) => {
    const currencyCode = EventHelper.eventEdition!.defaultCurrencyCode;
    const orderIds: string[] = [];
    const authCodes: string[] = [];
    const receiptProducts: ReceiptProduct[] = [];
    const payments: ReceiptBuilderProps["payments"] = [];
    let totalAmount = 0;
    let discountAmount = 0;

    options.partialPaymentsData!.paymentGroup.badges.forEach(paymentGroupBadge => {
      orderIds.push(paymentGroupBadge.orderId);
      const order = orders.find(order => order.orderId === paymentGroupBadge.orderId);
      if (order?.discountAmount) {
        discountAmount += order.discountAmount;
      }
      const foundBadge = getBadgeFromStoreById(paymentGroupBadge.badgeId)!;
      const orderSummaryRow = options.orderSummary!.find(orderSummary => orderSummary.product.sku === foundBadge.productCode)!;
      const foundProduct = products.find(storeProduct => storeProduct.sku === foundBadge.productCode)!;

      const badgesWithSameProduct = options
        .partialPaymentsData!.paymentGroup.badges.map(badge => {
          const badgeFromStore = getBadgeFromStoreById(badge.badgeId)!;
          if (badgeFromStore?.productCode === foundProduct.sku) {
            return `${badgeFromStore.contact.firstName} ${badgeFromStore.contact.lastName}`;
          }
          return null;
        })
        .filter(Boolean) as string[];

      const newReceiptProduct: ReceiptProduct = {
        price: orderSummaryRow.price,
        amount: orderSummaryRow.amount as number,
        name: foundProduct.name,
        badges: badgesWithSameProduct,
        sku: foundProduct.sku
      };
      if (!receiptProducts.find(product => product.sku === newReceiptProduct.sku)) {
        receiptProducts.push(newReceiptProduct);
      }
    });
    options.partialPaymentsData!.paymentGroup.payments.forEach(payment => {
      totalAmount += Number(payment.amount);
      payments.push({
        paymentMethod: getMappedPaymentMethod(payment.paymentMethod) as ReceiptBuilderProps["payments"][0]["paymentMethod"],
        amount: CurrencyHelper.moneyFormatWithoutZeros(Number(payment.amount), currencyCode, i18n.language)
      });
      authCodes.push(payment.reference);
    });
    const totalTax = CurrencyHelper.moneyFormatWithoutZeros(options.totalTax, currencyCode, i18n.language);
    const discount = CurrencyHelper.moneyFormatWithoutZeros(discountAmount, currencyCode, i18n.language);
    const totalAmountWithCurrency = CurrencyHelper.moneyFormatWithoutZeros(totalAmount, currencyCode, i18n.language);
    const totalExclTax = CurrencyHelper.moneyFormatWithoutZeros(totalAmount - options.totalTax, currencyCode, i18n.language);
    return {
      totalExclTax,
      products: receiptProducts,
      payments,
      orders: [...new Set(orderIds)],
      authCodes,
      discount: discountAmount ? `-${discount}` : undefined,
      tax: totalTax,
      total: totalAmountWithCurrency
    };
  };

  const getPropsForUpgrades = ({
    badge,
    upgradePrice,
    badgeUpgradeOptions,
    paymentMethod,
    authorisationCode,
    orderId,
    referenceNumber,
    customBadgeUpgradePrice
  }: UpgradeReceiptParams) => {
    const { value, currency } = upgradePrice;
    const price = CurrencyHelper.moneyFormatWithoutZeros(value, currency, i18n.language);
    const { firstName, lastName } = badge.contact;
    const magentoProduct = MagentoHelper.magentoProducts.find(product => product.sku === badgeUpgradeOptions?.newProductCode);
    let productPrice = price;
    const priceWithoutTax = badgeUpgradeOptions?.price.priceWithoutTax;

    const totalExclTax = priceWithoutTax ? CurrencyHelper.moneyFormatWithoutZeros(priceWithoutTax, currency, i18n.language) : "";

    if (customBadgeUpgradePrice) {
      productPrice = CurrencyHelper.moneyFormatWithoutZeros(customBadgeUpgradePrice, currency, i18n.language);
    }

    const discount = badgeUpgradeOptions?.price.discount
      ? CurrencyHelper.moneyFormatWithoutZeros(badgeUpgradeOptions.price.discount, currency, i18n.language)
      : undefined;
    const product: ReceiptProduct = {
      price: productPrice,
      amount: 1,
      name: magentoProduct?.name || "", // NOSONAR
      badges: [`${firstName} ${lastName}`],
      sku: magentoProduct?.sku || "" // NOSONAR
    };
    const tax = badgeUpgradeOptions!.price.priceWithTax - badgeUpgradeOptions!.price.priceWithoutTax;
    return {
      totalExclTax,
      orders: [orderId!],
      discount: discount ? `-${discount}` : discount,
      authCodes: [authorisationCode || referenceNumber!], // NOSONAR
      products: [product],
      payments: [{ paymentMethod: paymentMethod!, amount: price }],
      tax: CurrencyHelper.moneyFormatWithoutZeros(tax, currency, i18n.language),
      total: CurrencyHelper.moneyFormatWithoutZeros(badgeUpgradeOptions!.price.priceWithTax, currency, i18n.language)
    };
  };

  return { getPropsForUpgrades, getPropsForPartialPayments };
};
